import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articleData';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.slug, article.categorySlug);

  const breadcrumbItems = [
    { label: '分类浏览', path: '/category/all' },
    { label: article.category, path: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500 flex items-center">
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime}
                </span>
                <span className="text-xs text-foreground-500">作者：{article.author}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom max-w-none">
              {article.sections.map((section, index) => {
                switch (section.type) {
                  case 'heading':
                    const HeadingTag = section.level === 2 ? 'h2' : 'h3';
                    const headingClass = section.level === 2
                      ? 'font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4'
                      : 'font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-3';
                    return (
                      <HeadingTag key={index} className={headingClass}>
                        {section.content}
                      </HeadingTag>
                    );
                  case 'paragraph':
                    return (
                      <p key={index} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                        {section.content}
                      </p>
                    );
                  case 'list':
                    return (
                      <ul key={index} className="space-y-2 mb-5 ml-1">
                        {section.items?.map((item, i) => (
                          <li key={i} className="flex items-start text-foreground-700 text-sm md:text-base">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 mr-3 flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  case 'tip':
                    return (
                      <div key={index} className="bg-accent-50 border border-accent-200/60 rounded-lg p-4 md:p-5 mb-5">
                        <div className="flex items-start">
                          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 mr-3">
                            <i className="ri-lightbulb-line text-accent-500"></i>
                          </span>
                          <p className="text-foreground-700 text-sm leading-relaxed">{section.content}</p>
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-12 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <div className="flex items-center text-xs text-foreground-500 mt-auto">
                        <span>{related.date}</span>
                        <span className="mx-2">·</span>
                        <span>{related.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              datePublished: article.date,
              author: { '@type': 'Person', name: article.author },
              image: article.image,
              publisher: {
                '@type': 'Organization',
                name: '内裤精选指南',
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}